import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { tours } from '../data';
import { TourCard } from '../components/ui/Cards';
import './DetailPage.css';

export default function TourDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const tour = tours.find(t => t.slug === slug);

  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [date, setDate] = useState('');
  const [openTab, setOpenTab] = useState('overview');
  const { t } = useTranslation();

  if (!tour) return <div className="not-found-msg container">Tour not found. <Link to="/tours">Back to tours</Link></div>;

  const total = (adults * tour.price) + (children * (tour.priceChild || 0));
  const related = tours.filter(t => t.destination === tour.destination && t.id !== tour.id).slice(0, 3);

  const handleBook = () => {
    navigate(`/book?tour=${tour.slug}&adults=${adults}&children=${children}&date=${date}`);
  };

  const heroImages = {
    'atlas-mountains-day-trip':      'https://www.image2url.com/r2/default/images/1776889060236-37d4350c-4b74-4ad9-a6f2-2eb4d3930966.jpg',
    'agafay-desert-day-trip':        'https://images.unsplash.com/photo-1509316785289-025f5b846b35?w=600&q=80',
    'ouzoud-waterfalls-trip':        'https://www.image2url.com/r2/default/images/1776889330860-491f6402-d23c-4714-80b8-2b5485c43d64.jpg',
    'ait-ben-haddou-kasbah':         'https://images.unsplash.com/photo-1737927427538-9f9b4ea4ab02?fm=jpg&q=60&w=3000&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    'essaouira-day-trip':            'https://images.unsplash.com/photo-1624802704371-c76faa34c3a0?fm=jpg&q=60&w=3000&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    'ourika-valley-day-trip':        'https://images.unsplash.com/photo-1671804079626-4aaafa95184e?fm=jpg&q=60&w=3000&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    'marrakech-medina-guided-tour':  'https://images.unsplash.com/photo-1597212618440-806262de4f6b?fm=jpg&q=60&w=3000&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    'marrakech-food-tour':           'https://images.unsplash.com/photo-1643019237176-8ae0859f1123?fm=jpg&q=60&w=3000&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    'marrakech-casablanca-day-trip': 'https://images.unsplash.com/photo-1701945247920-7e4a461e08cb?fm=jpg&q=60&w=3000&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    'lake-takerkoust-asni-valley':   'https://www.image2url.com/r2/default/images/1776890677648-dad65ab7-ede1-4a42-8cb7-7b860349c01b.webp',
    'marrakech-photography-tour':    'https://images.unsplash.com/photo-1554048612-b6a482bc67e5?fm=jpg&q=60&w=3000&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    'shopping-in-the-souks':         'https://images.unsplash.com/photo-1587974928442-77dc3e0dba72?w=600&q=80',
    'marrakech-agadir-day-trip':     'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1600&q=80',
  };
  const heroImg = heroImages[tour.slug];

  return (
    <>
      <div className="detail-hero">
        <div className="detail-hero__img" style={{ background: tour.bgColor }}>
          {heroImg
            ? <img src={heroImg} alt={tour.name} />
            : <div style={{ width:'100%', height:'100%', background: `linear-gradient(135deg, ${tour.bgColor}, #1a1a2e)` }} />
          }
          {tour.badge && <span className="badge badge-primary detail-hero__badge">{tour.badge}</span>}
        </div>
      </div>

      <div className="container detail-layout">
        <div className="detail-main">
          <div className="breadcrumb">
            <Link to="/">{t('common.home')}</Link> <span>/</span>
            <Link to="/tours">{t('nav.tours')}</Link> <span>/</span>
            <span>{tour.name}</span>
          </div>

          <div className="detail-header">
            <div>
              <span className="badge badge-gold">{tour.category === 'city-tours' ? t('tours.cityTour') : t('tours.dayTrip')}</span>
              <h1 className="detail-title">{tour.name}</h1>
              <div className="detail-meta">
                <span className="stars">{'★'.repeat(Math.floor(tour.rating))}</span>
                <span className="detail-meta-text">{tour.rating} · {tour.reviews} reviews</span>
                <span className="detail-meta-sep">·</span>
                <span className="detail-meta-text">⏱ {tour.duration}</span>
                <span className="detail-meta-sep">·</span>
                <span className="detail-meta-text">👥 {tour.groupSize} people</span>
                <span className="detail-meta-sep">·</span>
                <span className="detail-meta-text">🌐 {tour.language.join(', ')}</span>
              </div>
            </div>
          </div>

          <div className="detail-tabs">
            {[
              { key: 'overview', label: t('detail.overview') },
              { key: 'itinerary', label: t('detail.itinerary') },
              { key: 'included', label: t('detail.included') },
            ].map(tab => (
              <button key={tab.key} className={`detail-tab${openTab === tab.key ? ' active' : ''}`} onClick={() => setOpenTab(tab.key)}>
                {tab.label}
              </button>
            ))}
          </div>

          {openTab === 'overview' && (
            <div className="detail-section">
              <p className="detail-desc">{tour.description}</p>
              <h3 className="detail-sub-title">{t('detail.highlights')}</h3>
              <ul className="highlight-list">
                {tour.highlights.map((h, i) => <li key={i}><span className="highlight-dot" />{h}</li>)}
              </ul>
              <div className="quick-facts">
                <div className="quick-fact"><span className="quick-fact__label">{t('detail.duration')}</span><span className="quick-fact__val">{tour.duration}</span></div>
                <div className="quick-fact"><span className="quick-fact__label">{t('detail.groupSize')}</span><span className="quick-fact__val">{tour.groupSize}</span></div>
                <div className="quick-fact"><span className="quick-fact__label">{t('detail.pickup')}</span><span className="quick-fact__val">{tour.pickup}</span></div>
                <div className="quick-fact"><span className="quick-fact__label">{t('detail.difficulty')}</span><span className="quick-fact__val">{tour.difficulty}</span></div>
                <div className="quick-fact"><span className="quick-fact__label">{t('detail.languages')}</span><span className="quick-fact__val">{tour.language.join(', ')}</span></div>
                <div className="quick-fact"><span className="quick-fact__label">{t('detail.cancellation')}</span><span className="quick-fact__val">{tour.cancellation}</span></div>
              </div>
            </div>
          )}

          {openTab === 'itinerary' && (
            <div className="detail-section">
              <h3 className="detail-sub-title">{t('detail.dayItinerary')}</h3>
              <div className="itinerary-list">
                {tour.itinerary.map((item, i) => (
                  <div key={i} className="itinerary-item">
                    <div className="itinerary-time">{item.time}</div>
                    <div className="itinerary-dot" />
                    <div className="itinerary-desc">{item.desc}</div>
                  </div>
                ))}
              </div>
              <div className="meeting-point">
                <h3 className="detail-sub-title">{t('detail.meetingPoint')}</h3>
                <p>{tour.meetingPoint}</p>
              </div>
            </div>
          )}

          {openTab === 'included' && (
            <div className="detail-section">
              <div className="inc-exc-grid">
                <div>
                  <h3 className="detail-sub-title included">{t('detail.whatIncluded')}</h3>
                  <ul className="inc-list inc">
                    {tour.included.map((item, i) => <li key={i}><span>✓</span>{item}</li>)}
                  </ul>
                </div>
                <div>
                  <h3 className="detail-sub-title excluded">{t('detail.notIncluded')}</h3>
                  <ul className="inc-list exc">
                    {tour.excluded.map((item, i) => <li key={i}><span>✗</span>{item}</li>)}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="detail-sidebar">
          <div className="booking-card">
            <div className="booking-card__stars">
              <span className="stars">{'★'.repeat(Math.floor(tour.rating))}</span>
              <span className="booking-card__reviews">{tour.reviews} reviews</span>
            </div>

            <div className="booking-field">
              <label>{t('booking.date')}</label>
              <input type="date" value={date} onChange={e => setDate(e.target.value)} min={new Date().toISOString().split('T')[0]} />
            </div>

            <div className="booking-field">
              <label>{t('booking.adults')}</label>
              <div className="counter">
                <button onClick={() => setAdults(Math.max(1, adults - 1))}>−</button>
                <span>{adults}</span>
                <button onClick={() => setAdults(adults + 1)}>+</button>
              </div>
            </div>

            {tour.priceChild > 0 && (
              <div className="booking-field">
                <label>{t('booking.children')}</label>
                <div className="counter">
                  <button onClick={() => setChildren(Math.max(0, children - 1))}>−</button>
                  <span>{children}</span>
                  <button onClick={() => setChildren(children + 1)}>+</button>
                </div>
              </div>
            )}

            <div className="booking-card__total">
              <span>{t('booking.price')}</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--primary)' }}>{t('booking.onRequest')}</span>
            </div>

            <button className="btn btn-primary booking-card__cta" onClick={handleBook}>{t('booking.bookNow')}</button>

            <a
              href={`https://wa.me/212694401220?text=Hi!%20I'd%20like%20to%20book:%20${encodeURIComponent(tour.name)}`}
              target="_blank" rel="noreferrer"
              className="btn booking-card__wa"
              style={{ background: '#25d366', color: '#fff', justifyContent: 'center' }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.553 4.116 1.522 5.852L0 24l6.313-1.492A11.954 11.954 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 01-5.006-1.37l-.36-.213-3.736.882.935-3.626-.234-.372A9.818 9.818 0 1112 21.818z"/></svg>
              {t('booking.bookViaWa')}
            </a>

            <p className="booking-card__cancel">🔄 {tour.cancellation}</p>
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <section className="section section-alt">
          <div className="container">
            <h2 className="section-title" style={{ marginBottom: 32 }}>You Might Also Like</h2>
            <div className="grid-3">
              {related.map(t => <TourCard key={t.id} tour={t} />)}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
